// marketingAnalytics — PostHog event names for the marketing upsell + activation
// surfaces (PE-1743 observability).
//
// SCOPE
//   The CharityStack-native marketing chrome: the Starter-org upsell (Marketing
//   Engagement / MarketingPaywall), sidebar discovery, generic entitlement locks,
//   and the tier-activation moment. It does NOT cover the marketing *activation
//   depth* taxonomy (marketing_campaign_sent, marketing_tutorial_*, etc.) — those
//   are specified in POCKETFLOW_OVERVIEW § Success Metrics and mostly fire from the
//   marketing backend (the PocketFlows iframe is cross-origin and unobservable from
//   here). The tour events already live as constants in marketingTourContent.ts.
//
// CONVENTION
//   Flat snake_case, domain-prefixed (`marketing_*`, `entitlement_*`) via
//   `safeCapture` — the dashboard convention, matching the existing marketing_tour_*
//   names. Aligns with the upsell events specified in the engagement-upsell doc
//   (marketing_upsell_viewed / marketing_upsell_cta_clicked).

import type { PostHog } from 'posthog-js';
import { safeCapture } from './analytics';

export const MARKETING_EVENTS = {
  /** A Starter/non-entitled org viewed an in-page marketing upsell (per surface). */
  UPSELL_VIEWED: 'marketing_upsell_viewed',
  /** A Starter/non-entitled org clicked a CTA on the upsell (upgrade / learn_more / contact). */
  UPSELL_CTA_CLICKED: 'marketing_upsell_cta_clicked',
  /**
   * @deferred — NOT fired yet. A marketing sidebar nav-item click (discovery
   * signal). Deferred in PE-1743: the sidebar renders through a shared generic
   * item renderer, and `marketing_upsell_viewed` already captures arrival at the
   * upsell, so this is a redundant-for-funnel nice-to-have. Kept in the catalog so
   * the follow-up that wires it doesn't reinvent the name. Until then it will show
   * zero occurrences in PostHog by design — see the observability doc's
   * "Deferred / conscious omissions".
   */
  NAV_CLICKED: 'marketing_nav_clicked',
  /**
   * The marketing tier went live for an org (enableMarketing succeeded). Cohort
   * start for all activation-window / time-to-first-send metrics. The FE-observable
   * half of the taxonomy in POCKETFLOW_OVERVIEW § Success Metrics.
   */
  TIER_ACTIVATED: 'marketing_tier_activated',
  // Segment Builder Redesign (SPEC §1 Tracking). Properties carry paths, template
  // keys, sections, kinds and bucketed counts only — never a name, rule value or id.
  /** The redesigned builder opened in create mode. `path: 'template'|'scratch'`, `templateKey?`. */
  SEGMENT_BUILDER_STARTED: 'segment_builder_started',
  /** First edit after the builder opened (once per session). `path`, `templateKey?`, `section`. */
  SEGMENT_RULES_EDITED: 'segment_rules_edited',
  /** "Reset to template" confirmed. `templateKey`. */
  SEGMENT_TEMPLATE_RESET: 'segment_template_reset',
  /** "See all" opened the people drawer. `tab`, `matchBucket`. */
  SEGMENT_PEOPLE_DRAWER_OPENED: 'segment_people_drawer_opened',
  /** Save acknowledged. `path`, `templateKey?`, `edited`, `kind`, `ruleCount`, `handPickedCount`, `fieldIds` (the rule field ids used, never values). */
  SEGMENT_SAVED: 'segment_saved',
  /** Cancel or leaving with unsaved changes. `path`, `templateKey?`, `edited`. */
  SEGMENT_BUILDER_ABANDONED: 'segment_builder_abandoned',
} as const;

export const ENTITLEMENT_EVENTS = {
  /** A generic gated-feature lock (UpgradeLock / usage meter) was shown to the user. */
  LOCK_VIEWED: 'entitlement_lock_viewed',
  /** The upgrade CTA on a generic entitlement lock was clicked. */
  UPGRADE_CLICKED: 'entitlement_upgrade_clicked',
} as const;

export type MarketingEvent = (typeof MARKETING_EVENTS)[keyof typeof MARKETING_EVENTS];
export type EntitlementEvent = (typeof ENTITLEMENT_EVENTS)[keyof typeof ENTITLEMENT_EVENTS];

/** Fire a marketing/entitlement event. Thin, name-constrained wrapper over safeCapture. */
export function trackMarketing(
  posthog: PostHog | undefined,
  event: MarketingEvent | EntitlementEvent,
  props: Record<string, unknown> = {},
): void {
  safeCapture(posthog, event, props);
}
